import { useCallback, useEffect, useRef, useState } from 'react'
import type JanusGateway from 'janus-gateway'
import type { JanusJS } from 'janus-gateway'
import type MpegtsJs from 'mpegts.js'

import type {
    CameraJanusLiveStream,
    CameraLegacyLiveStream,
} from '@/lib/api/vehicle-gps-api'
import { vehicleGpsApi } from '@/lib/api/vehicle-gps-api'
import { ApiError } from '@/lib/api'
import { cameraLiveCircuit } from '@/lib/camera-live-circuit'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export type CameraLiveState = 'idle' | 'starting' | 'playing' | 'error'

type JanusSession = InstanceType<typeof JanusGateway>
type MpegtsModule = typeof MpegtsJs
type MpegtsPlayer = ReturnType<MpegtsModule['createPlayer']>
type ActiveLiveStream =
    | { mode: 'janus'; stream: CameraJanusLiveStream }
    | { mode: 'legacy'; stream: CameraLegacyLiveStream }

const KEEP_ALIVE_MS = 5_000

function coerceJanusStreamID(streamID: string) {
    const n = Number(streamID)
    return Number.isFinite(n) && streamID.trim() !== '' ? n : streamID
}

// One live session per hook instance. Each camera tile calls this once, so N
// tiles hold N independent sessions — the VnetGPS API accepts any subset of
// camera ids and returns one stream per id.
export function useCameraLive(vehicleId: string, cameraId: number) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const janusRef = useRef<JanusSession | null>(null)
    const handleRef = useRef<JanusJS.PluginHandle | null>(null)
    const remoteStreamRef = useRef<MediaStream | null>(null)
    const liveStreamRef = useRef<ActiveLiveStream | null>(null)
    const mpegtsPlayerRef = useRef<MpegtsPlayer | null>(null)
    // Bumped by every start, stop and unmount. An attempt that is no longer
    // the latest must not create or keep a player: overlapping starts
    // (StrictMode re-running the auto-start effect, a retry, a double click)
    // otherwise attach two players to one <video> and orphan the first, which
    // keeps streaming into a MediaSource the second one replaced.
    const attemptRef = useRef(0)

    const [state, setState] = useState<CameraLiveState>('idle')
    const [error, setError] = useState('')

    const cleanup = useCallback(
        async (stopRemote: boolean) => {
            const active = liveStreamRef.current
            liveStreamRef.current = null
            if (stopRemote && active?.mode === 'janus') {
                try {
                    await vehicleGpsApi.stopCameraLive(vehicleId, {
                        index: active.stream.index,
                        stream_id: active.stream.stream_id,
                        node_id: active.stream.node_id,
                    })
                } catch {
                    // Best-effort: release local resources even if the remote
                    // stop call fails.
                }
            }

            if (mpegtsPlayerRef.current) {
                try {
                    mpegtsPlayerRef.current.unload()
                    mpegtsPlayerRef.current.detachMediaElement()
                    mpegtsPlayerRef.current.destroy()
                } catch {
                    // ignore local FLV cleanup failures
                }
                mpegtsPlayerRef.current = null
            }
            if (handleRef.current) {
                try {
                    handleRef.current.detach()
                } catch {
                    // ignore local Janus cleanup failures
                }
                handleRef.current = null
            }
            if (janusRef.current) {
                try {
                    janusRef.current.destroy({ cleanupHandles: true })
                } catch {
                    // ignore local Janus cleanup failures
                }
                janusRef.current = null
            }
            if (remoteStreamRef.current) {
                remoteStreamRef.current.getTracks().forEach((track) => {
                    track.stop()
                })
                remoteStreamRef.current = null
            }
            setState('idle')
        },
        [vehicleId],
    )

    useEffect(() => {
        return () => {
            attemptRef.current += 1
            void cleanup(true)
        }
    }, [cleanup])

    // Keep-alive is keyed on this instance's own stream id, so parallel
    // sessions never refresh each other's stream.
    useEffect(() => {
        const active = liveStreamRef.current
        if (!active || active.mode !== 'janus' || state !== 'playing') return

        const streamId = active.stream.stream_id
        const timer = window.setInterval(() => {
            void vehicleGpsApi.keepCameraLiveAlive(vehicleId, streamId)
        }, KEEP_ALIVE_MS)
        return () => window.clearInterval(timer)
    }, [state, vehicleId])

    const attachJanus = useCallback(
        async (stream: CameraJanusLiveStream, isCurrent: () => boolean) => {
            const Janus = (await import('janus-gateway')).default
            await new Promise<void>((resolve) => {
                Janus.init({ debug: false, callback: resolve })
            })
            if (!Janus.isWebrtcSupported()) {
                throw new Error(m.vehicle_camera_webrtc_unsupported())
            }

            const janus = await new Promise<JanusSession>((resolve, reject) => {
                const session: JanusSession = new Janus({
                    server: stream.address,
                    success: () => resolve(session),
                    error: reject,
                    destroyed: () => undefined,
                })
            })
            if (!isCurrent()) {
                janus.destroy({ cleanupHandles: true })
                return
            }
            janusRef.current = janus

            await new Promise<void>((resolve, reject) => {
                janus.attach({
                    plugin: 'janus.plugin.streaming',
                    success: (handle) => {
                        if (!isCurrent()) {
                            handle.detach()
                            resolve()
                            return
                        }
                        handleRef.current = handle
                        handle.send({
                            message: {
                                request: 'watch',
                                id: coerceJanusStreamID(stream.stream_id),
                            },
                        })
                        resolve()
                    },
                    error: reject,
                    onmessage: (message, jsep) => {
                        if (message.error) {
                            setError(String(message.error))
                            setState('error')
                        }
                        if (!jsep || !handleRef.current) return
                        handleRef.current.createAnswer({
                            jsep,
                            media: { audioSend: false, videoSend: false },
                            success: (answer) => {
                                handleRef.current?.send({
                                    message: { request: 'start' },
                                    jsep: answer,
                                })
                            },
                            error: (err) => {
                                setError(String(err))
                                setState('error')
                            },
                        })
                    },
                    onremotetrack: (track, _mid, on) => {
                        if (!remoteStreamRef.current) {
                            remoteStreamRef.current = new MediaStream()
                        }
                        if (on) {
                            remoteStreamRef.current.addTrack(track)
                            if (videoRef.current) {
                                videoRef.current.srcObject =
                                    remoteStreamRef.current
                                void videoRef.current
                                    .play()
                                    .catch(() => undefined)
                            }
                            setState('playing')
                        } else {
                            remoteStreamRef.current.removeTrack(track)
                        }
                    },
                    oncleanup: () => {
                        if (remoteStreamRef.current) {
                            remoteStreamRef.current
                                .getTracks()
                                .forEach((track) => {
                                    track.stop()
                                })
                            remoteStreamRef.current = null
                        }
                    },
                })
            })
        },
        [],
    )

    const attachLegacy = useCallback(
        async (stream: CameraLegacyLiveStream, isCurrent: () => boolean) => {
            const mpegts = (await import('mpegts.js')).default
            if (!mpegts.isSupported()) {
                throw new Error(m.vehicle_camera_live_error())
            }
            // Nothing awaits between this check and the ref assignment below, so a
            // newer attempt either sees this player in the ref (and its cleanup
            // destroys it) or this attempt bails before creating one.
            if (!isCurrent()) return
            if (!videoRef.current || !stream.url) {
                throw new Error(m.vehicle_camera_no_stream())
            }

            const player = mpegts.createPlayer(
                { type: 'flv', isLive: true, url: stream.url },
                {
                    enableWorker: true,
                    enableStashBuffer: false,
                    liveBufferLatencyChasing: true,
                },
            )
            mpegtsPlayerRef.current = player
            player.on(mpegts.Events.ERROR, (_type: string, detail: string) => {
                setError(detail || m.vehicle_camera_live_error())
                setState('error')
            })
            player.attachMediaElement(videoRef.current)
            player.load()
            await videoRef.current.play().catch(() => undefined)
            // Superseded while starting playback: whoever bumped the attempt
            // already ran cleanup on this player.
            if (!isCurrent()) return
            setState('playing')
        },
        [],
    )

    const start = useCallback(async () => {
        attemptRef.current += 1
        const attempt = attemptRef.current
        const isCurrent = () => attemptRef.current === attempt
        setState('starting')
        setError('')
        try {
            await cleanup(false)
            if (!isCurrent()) return
            setState('starting')
            const res = await vehicleGpsApi.startCameraLive(vehicleId, [
                cameraId,
            ])
            if (!isCurrent()) return
            if (res.data?.mode === 'legacy') {
                const stream = res.data.legacy_streams?.[0]
                if (!stream) throw new Error(m.vehicle_camera_no_stream())
                liveStreamRef.current = { mode: 'legacy', stream }
                await attachLegacy(stream, isCurrent)
                if (!isCurrent()) return
                cameraLiveCircuit.recordSuccess(vehicleId)
                return
            }
            const stream = res.data?.streams?.[0]
            if (!stream) throw new Error(m.vehicle_camera_no_stream())
            liveStreamRef.current = { mode: 'janus', stream }
            await attachJanus(stream, isCurrent)
            if (!isCurrent()) return
            cameraLiveCircuit.recordSuccess(vehicleId)
        } catch (err) {
            // A superseded attempt's failure belongs to nobody: recording it
            // or cleaning up would tear down the newer session.
            if (!isCurrent()) return
            const payload = formatApiError(
                err,
                err instanceof Error
                    ? err.message
                    : m.vehicle_camera_live_error(),
            )
            const message = payload.description
                ? `${payload.title} · ${payload.description}`
                : payload.title
            const kind = err instanceof ApiError ? err.kind : undefined
            cameraLiveCircuit.recordFailure(vehicleId, message, kind)
            setError(message)
            await cleanup(true)
            setState('error')
        }
    }, [attachJanus, attachLegacy, cameraId, cleanup, vehicleId])

    const stop = useCallback(async () => {
        attemptRef.current += 1
        await cleanup(true)
    }, [cleanup])

    return { state, error, videoRef, start, stop }
}
